import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { Avatar, IconButton, Badge } from './ui';
import { useAuth } from '../context/AuthContext';
import { notificationApi, companyApi, authorizationApi, orderApi } from '../api';

const NAV = [
  {
    group: 'Overview',
    items: [{ to: '/admin', icon: '◈', label: 'Dashboard', end: true }],
  },
  {
    group: 'Network',
    items: [
      { to: '/admin/companies', icon: '⬢', label: 'Companies', badge: 'pendingKyc' },
      { to: '/admin/users', icon: '⚇', label: 'Users' },
      { to: '/admin/authorizations', icon: '⛨', label: 'Authorizations', badge: 'pendingAuth' },
    ],
  },
  {
    group: 'Catalogue',
    items: [
      { to: '/admin/categories', icon: '☷', label: 'Categories' },
      { to: '/admin/brands', icon: '❖', label: 'Brands' },
      { to: '/admin/products', icon: '▦', label: 'Products' },
      { to: '/admin/approvals', icon: '✓', label: 'Listing approvals' },
      { to: '/admin/units', icon: '⚖', label: 'Selling units' },
      { to: '/admin/materials', icon: '⬗', label: 'Materials' },
      { to: '/admin/memberships', icon: '◈', label: 'Seller memberships' },
      { to: '/admin/inventory', icon: '⛁', label: 'Inventory' },
    ],
  },
  {
    group: 'Commerce',
    items: [
      { to: '/admin/orders', icon: '🛒', label: 'Orders', badge: 'newOrders' },
      // Contracts are hidden for now, matching the app. The page and its route
      // are still here — uncomment this line to bring the link back.
      // { to: '/admin/contracts', icon: '📄', label: 'Contracts' },
      { to: '/admin/payments', icon: '₹', label: 'Payments' },
      { to: '/admin/shipments', icon: '🚚', label: 'Shipments' },
      { to: '/admin/cancellations', icon: '⊘', label: 'Cancellations' },
    ],
  },
  {
    group: 'Support',
    items: [
      { to: '/admin/tickets', icon: '💬', label: 'Support requests' },
      { to: '/admin/reasons', icon: '☰', label: 'Cancel & return reasons' },
    ],
  },
  {
    group: 'Risk & Capital',
    items: [
      { to: '/admin/claims', icon: '⚠', label: 'Claims' },
      { to: '/admin/insurance', icon: '🛡', label: 'Insurance' },
      { to: '/admin/finance', icon: '🏦', label: 'Finance' },
    ],
  },
  {
    group: 'Platform',
    items: [
      { to: '/admin/reports', icon: '📊', label: 'Reports' },
      { to: '/admin/notifications', icon: '🔔', label: 'Notifications' },
      { to: '/admin/settings', icon: '⚙', label: 'Settings' },
    ],
  },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [counts, setCounts] = useState({});
  const [unread, setUnread] = useState(0);

  // Close the drawer and the profile menu whenever the route changes.
  useEffect(() => {
    setOpen(false);
    setMenu(false);
  }, [location.pathname]);

  /** Sidebar badges — refreshed on navigation so they stay honest. */
  useEffect(() => {
    let alive = true;

    Promise.allSettled([
      companyApi.list({ verificationStatus: 'pending', limit: 1 }),
      authorizationApi.stats(),
      orderApi.list({ status: 'placed', limit: 1 }),
      notificationApi.unreadCount(),
    ]).then(([kyc, auth, orders, notif]) => {
      if (!alive) return;
      setCounts({
        pendingKyc: kyc.status === 'fulfilled' ? kyc.value.meta.total : 0,
        pendingAuth: auth.status === 'fulfilled' ? auth.value.data.pending : 0,
        newOrders: orders.status === 'fulfilled' ? orders.value.meta.total : 0,
      });
      if (notif.status === 'fulfilled') setUnread(notif.value.data.count);
    });

    return () => {
      alive = false;
    };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/loginadmin', { replace: true });
  };

  return (
    <div className="shell">
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-head">
          <Logo size={30} variant="light" />
        </div>

        <nav className="sidebar-nav">
          {NAV.map(section => (
            <div key={section.group}>
              <div className="nav-group-label">{section.group}</div>
              {section.items.map(item => {
                const count = item.badge ? counts[item.badge] : 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  >
                    <span className="ico">{item.icon}</span>
                    <span>{item.label}</span>
                    {count > 0 && <span className="count">{count > 99 ? '99+' : count}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          Tarpex.live Admin · v1.0.0
          <br />
          Built by Stackerio
        </div>
      </aside>

      {open && <div className="scrim" onClick={() => setOpen(false)} />}

      <div className="main">
        <header className="topbar">
          <IconButton className="burger" icon="☰" onClick={() => setOpen(v => !v)} title="Menu" />

          <div className="grow" />

          <IconButton
            icon="🔔"
            title="Notifications"
            onClick={() => navigate('/admin/notifications')}
            style={{ position: 'relative' }}
          >
            {unread > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  background: 'var(--danger)',
                  color: '#fff',
                  borderRadius: 99,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '1px 5px',
                  border: '2px solid #fff',
                }}
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </IconButton>

          <div style={{ position: 'relative' }}>
            <button
              type="button"
              className="row"
              onClick={() => setMenu(v => !v)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <Avatar src={user?.avatar} name={user?.name} size="sm" />
              <div style={{ textAlign: 'left', lineHeight: 1.25 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{user?.name || 'Admin'}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>Super Admin</div>
              </div>
              <span className="text-muted" style={{ fontSize: 10 }}>▼</span>
            </button>

            {menu && (
              <div
                className="card animate-fade"
                style={{ position: 'absolute', right: 0, top: 46, width: 240, zIndex: 50, padding: 6 }}
              >
                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ fontWeight: 600 }}>{user?.name}</div>
                  <div className="text-muted text-sm">{user?.email || user?.mobile}</div>
                  <div className="mt-1"><Badge status="approved">Super Admin</Badge></div>
                </div>
                <button
                  type="button"
                  className="nav-item"
                  style={{ color: 'var(--danger)', width: '100%', marginTop: 4, background: 'none', border: 'none', cursor: 'pointer' }}
                  onClick={handleLogout}
                >
                  <span className="ico">⏻</span> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="page">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
